// Canvas & sails: reads and writes for /canvas (docs/canvas-plan.md). The rules for what a move
// changes are in canvasCore.js; this only talks to the database.
//
//   /canvas/pieces/{id}       one piece (indexed on Boat_ID, Status and Installed_At); no history
//   /canvas/log/{id}/{key}    that piece's moves, only ever added to. Kept apart so the board,
//                             which lists every piece off its boat, never downloads anyone's history.
//   /canvas/spots/{key}       storage spots people have used, { Name, Used }: the spot box's suggestions
//   Storage canvas/{id}/…     a photo of the piece (the latest is the piece's Photo)

import { database, storage } from './initFirebase';
import { ref, get, push as newRef, query, orderByChild, equalTo, startAt, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { update } from './trackedDb'; // durable offline writes
import { applyAction, newPiece, planBatch, seasonStartMs, newRepair, answerRepair, repairSnapshot, loftInvoiceLine, isCanvasOp, money, ERRORS } from './canvasCore';
import { formatDateToYYYYMMDD } from './date-format';

const PIECES = 'canvas/pieces';
const LOG = 'canvas/log';
const LOFT_WORDS = /sail|canvas|loft|uphol|cushion|cover/i;

export const today = () => formatDateToYYYYMMDD(new Date());

export function currentUserName() {
  try { return localStorage.getItem('currentUserName') || ''; } catch { return ''; }
}

const list = snap => Object.entries(snap.val() || {}).map(([id, p]) => ({ id, ...p }));
const boatQuery = boatId => query(ref(database, PIECES), orderByChild('Boat_ID'), equalTo(String(boatId)));
const logKey = id => newRef(ref(database, `${LOG}/${id}`)).key;

/** This boat's pieces: [{ id, ...piece }]. */
export async function loadBoatPieces(boatId) {
  return list(await get(boatQuery(boatId)));
}

/** Calls fn(pieces) now and on every change to this boat's pieces. Returns the off function. */
export function watchBoatPieces(boatId, fn, onError = () => {}) {
  return onValue(boatQuery(boatId), snap => fn(list(snap)), onError);
}

/**
 * The board's pieces: everything off its boat (stored, out, returned) plus what went back on this
 * season. Two indexed queries, so pieces that have simply stayed aboard are never downloaded.
 * Calls fn(pieces) on every change; returns the off function.
 */
export function watchBoard(fn, onError = () => {}) {
  let off = [];
  let away = null;
  let back = null;
  const emit = () => {
    if (!away || !back) return;
    const byId = new Map([...back, ...away].map(p => [p.id, p])); // 'on-boat' sorts before 'out'
    fn([...byId.values()]);
  };
  // Status order: 'on-boat' < 'out' < 'returned' < 'stored', so startAt('out') is every piece off its boat.
  off.push(onValue(query(ref(database, PIECES), orderByChild('Status'), startAt('out')), snap => { away = list(snap); emit(); }, onError));
  off.push(onValue(query(ref(database, PIECES), orderByChild('Installed_At'), startAt(seasonStartMs(today()))),
    snap => { back = list(snap).filter(p => p.Status === 'on-boat'); emit(); }, onError));
  return () => { off.forEach(o => o()); off = []; };
}

/** Calls fn(pieces) now and on every change to the pieces out at a loft (one indexed query). */
export function watchOutAtLoft(fn, onError = () => {}) {
  return onValue(query(ref(database, PIECES), orderByChild('Status'), equalTo('out')), snap => fn(list(snap)), onError);
}

/** A piece's moves, { key: entry }. */
export async function loadLog(pieceId) {
  return (await get(ref(database, `${LOG}/${pieceId}`))).val() || {};
}

/** Several pieces' moves: { pieceId: { key: entry } } (a boat's Service history). */
export async function loadLogs(pieceIds) {
  const pairs = await Promise.all(pieceIds.map(async id => [id, await loadLog(id).catch(() => ({}))]));
  return Object.fromEntries(pairs);
}

const spotKey = name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
function spotWrite(updates, name, at) {
  const key = spotKey(name || '');
  if (key) updates[`canvas/spots/${key}`] = { Name: name, Used: at };
}

/** Storage spots used before, most recently used first. */
export async function loadSpots() {
  const snap = await get(ref(database, 'canvas/spots'));
  return Object.values(snap.val() || {}).filter(s => s && s.Name).sort((a, b) => (b.Used || 0) - (a.Used || 0)).map(s => s.Name);
}

let vendorsPromise = null;
/** Vendors for the "Send out to" list (read once per page): canvas and sail lofts first. */
export function loadLofts() {
  if (!vendorsPromise) {
    vendorsPromise = get(ref(database, 'settings/vendorList')).then(snap => Object.entries(snap.val() || {})
      .filter(([, v]) => v && v.Vendor_Name)
      .map(([id, v]) => ({ id, name: v.Vendor_Name, loft: LOFT_WORDS.test(v.Vendor_Name) }))
      .sort((a, b) => (b.loft - a.loft) || a.name.localeCompare(b.name)))
      .catch(err => { vendorsPromise = null; throw err; });
  }
  return vendorsPromise;
}

/** A new piece's id (before it is written, so a photo can be filed under it). */
export const newPieceId = () => newRef(ref(database, PIECES)).key;

/** Add a piece. `fields` as canvasCore.newPiece takes them, plus an optional `id`. Returns its id. */
export async function addPiece(fields) {
  const piece = newPiece(fields);
  const id = fields.id || newPieceId();
  const at = Date.now();
  const log = { At: at, By: currentUserName(), Action: 'add', From: '', To: piece.Status };
  if (piece.Location) log.Location = piece.Location;
  if (piece.WO) log.WO = piece.WO;
  if (fields.note) log.Note = String(fields.note).slice(0, 500);
  const updates = {
    [`${PIECES}/${id}`]: { ...piece, Created_At: at },
    [`${LOG}/${id}/${logKey(id)}`]: log,
  };
  spotWrite(updates, piece.Location, at);
  await update(ref(database), updates);
  return id;
}

function moveWrites(updates, { piece, patch, log }) {
  Object.entries(patch).forEach(([k, v]) => { updates[`${PIECES}/${piece.id}/${k}`] = v; });
  updates[`${LOG}/${piece.id}/${logKey(piece.id)}`] = log;
  if (log.Location) spotWrite(updates, log.Location, log.At);
}

/** Do a move (canvasCore ACTIONS) to a piece: only the changed fields plus one new log entry. */
export async function runAction(piece, action, input) {
  const at = Date.now();
  const move = { piece, ...applyAction(piece, action, input, { by: currentUserName(), at, today: today() }) };
  const updates = {};
  moveWrites(updates, move);
  await update(ref(database), updates);
  return move;
}

/**
 * The same move for several pieces, in one write. Returns { moved: [piece], refused: [{ piece, err }] }.
 */
export async function runBatch(pieces, action, input) {
  const { moves, refused } = planBatch(pieces, action, input, { by: currentUserName(), at: Date.now(), today: today() });
  if (moves.length) {
    const updates = {};
    moves.forEach(m => moveWrites(updates, m));
    await update(ref(database), updates);
  }
  return { moved: moves.map(m => ({ ...m.piece, ...m.patch })), refused };
}

/** Change what the piece is (kind, label): only the fields given. */
export function editPiece(id, fields) {
  const updates = {};
  Object.entries(fields).forEach(([k, v]) => { updates[`${PIECES}/${id}/${k}`] = v; });
  return update(ref(database), updates);
}

/** Delete a piece added by mistake, with its history. */
export function removePiece(id) {
  return update(ref(database), { [`${PIECES}/${id}`]: null, [`${LOG}/${id}`]: null });
}

/* ---------- repairs and the loft's invoice (session 3) ---------- */

const logWrite = (updates, piece, entry) => {
  updates[`${LOG}/${piece.id}/${logKey(piece.id)}`] = { At: Date.now(), By: currentUserName(), From: piece.Status || 'on-boat', To: piece.Status || 'on-boat', ...entry };
};

/** Note a repair the loft found (waits for the customer's answer). Returns its id. */
export async function addRepair(piece, { note, quote }) {
  const rep = newRepair({ note, quote, by: currentUserName() });
  const rid = newRef(ref(database, `${PIECES}/${piece.id}/Repairs`)).key;
  const updates = { [`${PIECES}/${piece.id}/Repairs/${rid}`]: rep };
  logWrite(updates, piece, { Action: 'repairAdd', Repair: rep.Note, ...(rep.Quote_Cents != null ? { Quote_Cents: rep.Quote_Cents } : {}) });
  await update(ref(database), updates);
  return rid;
}

/** Take back a repair note that hasn't gone to the customer yet. */
export async function removeRepair(piece, rid) {
  const rep = piece.Repairs?.[rid];
  if (!rep || rep.Status !== 'needs-approval') throw Object.assign(new Error('Only a repair not yet sent to the customer can be removed.'), { canvasCode: 'msg' });
  const updates = { [`${PIECES}/${piece.id}/Repairs/${rid}`]: null };
  logWrite(updates, piece, { Action: 'repairRemove', Repair: rep.Note });
  await update(ref(database), updates);
}

/** The office records an answer it got by phone or in person (the emailed link records its own). */
export async function recordRepairAnswer(piece, rid, decision, via) {
  const rep = piece.Repairs?.[rid];
  const by = currentUserName();
  const patch = answerRepair(rep, decision, { via, by });
  const updates = {};
  Object.entries(patch).forEach(([k, v]) => { updates[`${PIECES}/${piece.id}/Repairs/${rid}/${k}`] = v; });
  logWrite(updates, piece, { Action: 'repairAnswer', Decision: decision, Via: via, Who: by, Repair: rep.Note });
  await update(ref(database), updates);
}

/** Email the boat's waiting repairs to the customer with an approve/decline link (functions/signLinks.js). */
export async function emailRepairs(piece, { pieces, boatName = '' }) {
  const [{ openCompose, customerContact }, { getFunctions, httpsCallable }, { app }] = await Promise.all([
    import('./mailClient'), import('firebase/functions'), import('./initFirebase'),
  ]);
  const snap = repairSnapshot(pieces.filter(p => p.Boat_ID === piece.Boat_ID));
  if (!snap) throw Object.assign(new Error('No repairs on this boat are waiting for an answer.'), { canvasCode: 'msg' });
  const contact = await customerContact(piece.Customer_ID);
  const call = httpsCallable(getFunctions(app), 'createSignLink', { timeout: 60000 });
  const n = snap.items.length;
  return openCompose({
    to: contact.email, templateId: 'canvasRepairs', customerId: piece.Customer_ID,
    recordType: 'canvasRepairs', recordId: piece.Boat_ID,
    vals: { repair_count: `${n} repair${n === 1 ? '' : 's'}`, total: money(snap.totalCents), ...(boatName ? { boat: boatName } : {}) },
    // Made only when the email actually goes out; the repairs are then marked "sent".
    links: { sign_link: () => call({ kind: 'repair', customerId: piece.Customer_ID, boatId: piece.Boat_ID }).then(r => r.data?.url) },
  });
}

/**
 * The loft's invoice: a hand-priced line on the work order (on that WO's canvas operation for this
 * boat, else its first open operation for the boat), kept on the piece too. Returns the op it went on.
 */
export async function addLoftInvoice(piece, { vendorId, vendorName, invoiceNo, cost, charge, wo }) {
  const line = loftInvoiceLine(piece, { vendorName, invoiceNo, cost, charge });
  const woNo = String(wo || '').trim();
  if (!woNo) throw Object.assign(new Error(ERRORS['need-wo'].EN), { canvasCode: 'need-wo' });
  // The owner's operations, from the server (one customer's, by index): a device copy can lag behind.
  const all = (await get(query(ref(database, 'data'), orderByChild('Customer_ID'), equalTo(String(piece.Customer_ID))))).val() || {};
  const ops = Object.entries(all).map(([id, j]) => ({ ...j, id })).filter(j => String(j.Work_Order) === woNo && j.Boat_ID === piece.Boat_ID);
  const open = ops.filter(j => !j.Billing_Completed && !['Closed', 'Billed'].includes(j.Status));
  const op = open.find(isCanvasOp) || open[0];
  // A plain refusal for the person (canvasCode marks it as one, so it isn't logged as a fault).
  if (!op) throw Object.assign(new Error(ops.length ? `Every operation for this boat on WO ${woNo} is already billed.` : `No operation for this boat on WO ${woNo}.`), { canvasCode: 'msg' });
  const at = Date.now();
  const by = currentUserName();
  const partId = newRef(ref(database, `data/${op.id}/Parts`)).key;
  const cid = newRef(ref(database, `${PIECES}/${piece.id}/Costs`)).key;
  const updates = {
    [`data/${op.id}/Parts/${partId}`]: {
      ...line.part, Category: op.Category || '', Op_Type: op.Op_Type || '', Job_ID: op.id, ID: partId,
      Boat_Name: op.Boat_Name || '', Customer_Name: op.Customer_Name || '', Work_Order: op.Work_Order, Tech: by,
      Time_Submitted: new Date(at).toLocaleDateString('en-US'), Canvas_Piece: piece.id,
    },
    [`${PIECES}/${piece.id}/Costs/${cid}`]: {
      Vendor_ID: vendorId || piece.Vendor_ID || '', Vendor_Name: line.loft, Invoice_No: line.invoiceNo, Cost_Cents: line.costCents,
      Charge_Cents: line.chargeCents, WO: woNo, Job_ID: op.id, Part_ID: partId, Added_At: at, Added_By: by,
    },
  };
  logWrite(updates, piece, { Action: 'cost', Invoice_No: line.invoiceNo, Cost_Cents: line.costCents, WO: woNo, Vendor_Name: line.loft });
  await update(ref(database), updates);
  return op;
}

/** Where a piece's photo goes in Storage. */
export const photoPath = id => `canvas/${id}/${Date.now()}.jpg`;

/** Upload a photo and make it the piece's photo (online; the techs app queues its own when offline). */
export async function savePhoto(id, file, path = photoPath(id)) {
  await uploadBytes(storageRef(storage, path), file);
  await update(ref(database), { [`${PIECES}/${id}/Photo`]: path });
  return path;
}

const photoUrls = new Map();
/** A photo's download URL (remembered, so a redraw never refetches it). */
export function photoUrl(path) {
  if (!photoUrls.has(path)) photoUrls.set(path, getDownloadURL(storageRef(storage, path)).catch(() => ''));
  return photoUrls.get(path);
}
